import React, {forwardRef, PropsWithChildren, ReactNode, useContext} from 'react';
import styled from 'styled-components';

import {Renderer, RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {PushContext} from '../../../../../libs/shared/core/src/services/push/pushContext';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  LegacyInteractionParent,
  LegacyInteractionParentProps,
} from '../../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {NavigationHeader} from '../../../common/navigation/navigationHeader';
import {useOnEditWorkspace} from './workspaceHeaderHelpers';
import {WorkspaceHeaderMore, WorkspaceHeaderMoreDropdownRenderProps} from './workspaceHeaderMore';
import {WorkspacesHeaderOfflineIcon} from './workspacesHeaderOfflineIcon';

/*
 * Props.
 */

interface WorkspaceHeaderProps
  extends LegacyInteractionParentProps,
    Pick<WorkspaceHeaderMoreDropdownRenderProps, 'extraItems' | 'onHideWorkspace' | 'hideWorkspaceLabel'> {
  panelId: InboxesPanelId;
  isExpanded?: boolean;
  onToggleExpandClick?: () => void;
  isEditingWorkspace?: boolean;
  hasHover?: boolean;
  /** Override control of the actions menu button and dropdown. */
  renderActionsMenu?: RendererOf<WorkspaceHeaderMoreDropdownRenderProps>;
  renderTooltip?: Renderer;
  children: ReactNode;
}

/*
 * Style.
 */

const StyledMoreDiv = styled.div`
  grid-area: more;
  display: flex;
`;

/*
 * Component.
 */

export const WorkspaceHeader = forwardRef<HTMLDivElement, PropsWithChildren<WorkspaceHeaderProps>>(
  (props, ref) => {
    const {children, onToggleExpandClick, interactionId, isExpanded} = props;

    const {isConnected} = useContext(PushContext);

    const onEditWorkspace = useOnEditWorkspace(props);

    return (
      <LegacyInteractionParent interactionId={`workspaceheader-${interactionId}`}>
        <NavigationHeader
          ref={ref}
          isExpanded={isExpanded}
          onToggleExpandClick={onToggleExpandClick}
          more={maybeRenderMoreContainer(props, onEditWorkspace, !isConnected)}
          renderTooltip={props.renderTooltip}
        >
          {children}
        </NavigationHeader>
      </LegacyInteractionParent>
    );
  },
);

function maybeRenderMoreContainer(
  props: WorkspaceHeaderProps,
  onEditWorkspace: () => void,
  isOffline: boolean,
) {
  const {isEditingWorkspace, renderActionsMenu} = props;
  if (isEditingWorkspace) {
    return null;
  }

  return (
    <StyledMoreDiv>
      {isOffline ? (
        <WorkspacesHeaderOfflineIcon />
      ) : (
        renderActionsMenu?.({...props, onEditWorkspace}) ?? (
          <WorkspaceHeaderMore
            hideWorkspaceLabel={props.hideWorkspaceLabel}
            onHideWorkspace={props.onHideWorkspace}
            onEditWorkspace={onEditWorkspace}
            extraItems={props.extraItems}
          />
        )
      )}
    </StyledMoreDiv>
  );
}
